import { useCallback, useState, useMemo } from 'react';
import { Avatar, Chip, Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { ChartSettingsButton } from './ChartSettingsButton';
import { HiroChartHeader } from '../HiroChartHeader';
import { HiroInlineControls } from './HiroInlineControls';
import { isZerohedge } from '../../../../util';
import useHiroUi from '../../../../hooks/hiro/useHiroUi';
import { SizeControls } from '../../../shared/SizeControls';
import { InfoButton } from '../../../shared';
import { useHiroCtx } from '../../HiroProvider';
import CanvasSymSelector from 'components/canvas/components/CanvasSymSelector';
import { hiroSymbolsState } from 'states';
import { CanvasContentId } from 'types/canvas';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import { ExpandableContentButton } from 'components/shared';

export const HiroSettingsBar = () => {
  const [openSettings, _setOpenSettings] = useState(false);
  const [openSizeControls, _setOpenSizeControls] = useState(false);

  const { showInlineControls } = useHiroUi();
  const inlineChartControls = showInlineControls && (
    <HiroInlineControls isInline={true} />
  );

  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const hiroSymsArray = useMemo(
    () =>
      Array.from(hiroSyms ?? []).map((symbol) => ({
        symbol,
      })),
    [hiroSyms],
  );

  const { mode, sym, update, canvasId, expandableContentButtonType } =
    useHiroCtx();

  const setOpenSettings = useCallback((open: boolean) => {
    _setOpenSettings(open);
    _setOpenSizeControls(false);
  }, []);

  const setOpenSizeControls = useCallback((open: boolean) => {
    _setOpenSizeControls(open);
    _setOpenSettings(false);
  }, []);

  return (
    <Box
      sx={{
        marginBottom: '2px',
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
      }}
    >
      <Box>
        {mode === 'standalone' ? (
          <HiroChartHeader />
        ) : (
          <CanvasSymSelector
            sym={sym}
            options={hiroSymsArray}
            onSymChange={(sym) => update({ sym })}
            textFieldStyleOverrides={{ width: '15ex', marginRight: '1.5ex' }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          flexGrow: 1,
          justifyContent: 'flex-begin',
          flexWrap: 'wrap',
          maxHeight: '5ex',
          paddingTop: '1ex',
          rowGap: '20ex',
          overflow: 'hidden',
        }}
      >
        {mode === 'canvas' &&
          canvasId != null &&
          expandableContentButtonType != null && (
            <>
              <ExpandableContentButton type={expandableContentButtonType} />
              <CanvasGroupLinkButton
                componentId={canvasId}
                contentId={CanvasContentId.HIRO_CHART}
              />
            </>
          )}
        {!isZerohedge() && (
          <InfoButton key="hiro-info" articleKey="hiro-info" />
        )}
        {!isZerohedge() && mode === 'standalone' && (
          <SizeControls open={openSizeControls} setOpen={setOpenSizeControls} />
        )}
        {!isZerohedge() && (
          <ChartSettingsButton
            openSettings={openSettings}
            setOpenSettings={setOpenSettings}
          />
        )}
        {isZerohedge() && (
          <Chip
            avatar={<Avatar src={`images/hiro-logos/${sym}.png`} />}
            label={sym}
            size="small"
            variant="outlined"
            sx={{
              background: 'transparent',
              borderColor: '#18bdac',
              '&..MuiChip-label': {
                color: '#000000 !important',
              },
            }}
            className="zerohedge-ticker-chip"
          />
        )}
        {inlineChartControls}
      </Box>
    </Box>
  );
};
